import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SignupProcessing } from '@/components/SignupProcessing';
import { ModernAuthLayout } from '@/components/auth/ModernAuthLayout';
const Auth = () => {
  const {
    user,
    signIn,
    signUp,
    signUpUser,
    checkInvitation,
    loading
  } = useAuth();
  const {
    toast
  } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null);
  const [defaultTab, setDefaultTab] = useState("signin");
  const [defaultSignupTab, setDefaultSignupTab] = useState("admin");
  const [userEmail, setUserEmail] = useState("");
  const [hasValidInvitation, setHasValidInvitation] = useState<boolean | null>(null);
  const [checkingInvitation, setCheckingInvitation] = useState(false);
  const [isSignupProcessing, setIsSignupProcessing] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);

  // Handle magic link parameters
  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    if (token && email) {
      setInvitationToken(token);
      setInvitationEmail(email);
      setUserEmail(email);
      setHasValidInvitation(true);
      setDefaultTab("signup");
      setDefaultSignupTab("user");
    }
  }, [searchParams]);

  // Handle auth state changes
  useEffect(() => {
    if (user && (isSignupProcessing || signupComplete)) {
      // User is now authenticated, reset processing state
      console.log('User authenticated, resetting processing state');
      setIsSignupProcessing(false);
      setSignupComplete(false);
    }
  }, [user, isSignupProcessing, signupComplete]);

  // Fallback: if signup is complete but user is not authenticated after 5 seconds, show manual sign-in option
  useEffect(() => {
    if (signupComplete && !user) {
      const timer = setTimeout(() => {
        console.log('Signup complete but user not authenticated, showing manual sign-in option');
        setSignupComplete(false);
        setIsSignupProcessing(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [signupComplete, user]);

  // Check invitation when user email changes
  const handleEmailChange = async (email: string) => {
    setUserEmail(email);
    if (email && email.includes('@')) {
      setCheckingInvitation(true);
      try {
        const {
          hasInvitation,
          invitation
        } = await checkInvitation(email);
        setHasValidInvitation(hasInvitation);
        if (hasInvitation && invitation) {
          setInvitationToken(invitation.invitation_token);
        }
      } catch (error) {
        setHasValidInvitation(false);
      } finally {
        setCheckingInvitation(false);
      }
    } else {
      setHasValidInvitation(null);
    }
  };

  // Redirect if already authenticated
  if (user && !loading) {
    return <Navigate to="/" replace />;
  }
  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const {
      error
    } = await signIn(email, password);
    if (error) {
      setError(error.message);
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in."
      });
    }
    setIsLoading(false);
  };
  const handleSignUp = async () => {
    setIsLoading(true);
    setIsSignupProcessing(true);
    setError(null);

    // Get form data from the admin form
    const nameInput = document.getElementById('admin-signup-name') as HTMLInputElement;
    const companyInput = document.getElementById('admin-signup-company') as HTMLInputElement;
    const emailInput = document.getElementById('admin-signup-email') as HTMLInputElement;
    const designationInput = document.getElementById('admin-signup-designation') as HTMLInputElement;
    const passwordInput = document.getElementById('admin-signup-password') as HTMLInputElement;
    const email = emailInput.value;
    const password = passwordInput.value;
    const name = nameInput.value;
    const company = companyInput.value;
    const designation = designationInput.value;
    const {
      error
    } = await signUp(email, password, name, company, designation);
    if (error) {
      setError(error.message);
      setIsSignupProcessing(false);
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setSignupComplete(true);
      toast({
        title: "Welcome to ManageAze!",
        description: "Your company has been created successfully. Redirecting to dashboard..."
      });
    }
    setIsLoading(false);
  };
  const handleUserSignUp = async () => {
    setIsLoading(true);
    setIsSignupProcessing(true);
    setError(null);

    // Get form data from the user form
    const nameInput = document.getElementById('user-signup-name') as HTMLInputElement;
    const emailInput = document.getElementById('user-signup-email') as HTMLInputElement;
    const passwordInput = document.getElementById('user-signup-password') as HTMLInputElement;
    const tokenInput = document.getElementById('user-signup-token') as HTMLInputElement;
    const email = emailInput.value;
    const password = passwordInput.value;
    const name = nameInput.value;
    const invitationToken = tokenInput.value;
    const {
      error
    } = await signUpUser(email, password, name, '', invitationToken);
    if (error) {
      setError(error.message);
      setIsSignupProcessing(false);
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setSignupComplete(true);
      toast({
        title: "Welcome to the team!",
        description: "You have successfully joined the company. Redirecting to dashboard..."
      });
      // The user will be automatically redirected by the auth state change
    }
    setIsLoading(false);
  };
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>;
  }

  // Show processing state for any signup
  if (isSignupProcessing || signupComplete) {
    return <SignupProcessing isProcessing={isSignupProcessing} isComplete={signupComplete} message={signupComplete ? "Your account has been created and you're being redirected to the dashboard..." : undefined} />;
  }

  // If user is authenticated, redirect to dashboard
  if (user) {
    return <Navigate to="/" replace />;
  }
  return (
    <ModernAuthLayout
      isLoading={isLoading}
      error={error}
      userEmail={userEmail}
      hasValidInvitation={hasValidInvitation}
      checkingInvitation={checkingInvitation}
      invitationEmail={invitationEmail}
      invitationToken={invitationToken}
      defaultTab={defaultTab}
      defaultSignupTab={defaultSignupTab}
      handleSignIn={handleSignIn}
      handleSignUp={handleSignUp}
      handleUserSignUp={handleUserSignUp}
      handleEmailChange={handleEmailChange}
    />
  );
};
export default Auth;